import type { ImpersonationInfo } from "@/context/ProfileContext";

/**
 * The subset of Firebase's `IdTokenResult` we need to decide whether the
 * current session is an admin impersonation session.
 */
export interface ImpersonationTokenResult {
  /** Decoded token claims (custom + reserved). */
  claims: Record<string, unknown>;
  /**
   * How the session was established. Admin impersonation always signs in via
   * `signInWithCustomToken`, which yields `"custom"`. A member signing in with
   * their own credentials yields `"password"`, `"google.com"`, etc.
   */
  signInProvider: string | null;
}

/**
 * Decide whether a token represents an active admin impersonation session.
 *
 * Impersonation is stored as persistent custom claims (`impersonatedBy`, ...)
 * on the *member's own* Firebase Auth account so the claims survive ID-token
 * refresh while the admin stays signed in. The downside is that those claims
 * linger on the real member's account until they are explicitly cleared — and
 * if that cleanup ever fails (network error, admin closing the tab without
 * ending the session, etc.), the member would otherwise see the impersonation
 * banner and be treated as impersonated in their *own* session.
 *
 * The safeguard: only a session created through the impersonation handoff
 * (`signInWithCustomToken`, `signInProvider === "custom"`) may ever surface
 * impersonation. When the member signs in with their own credentials the
 * provider is never `"custom"`, so stale claims can never leak into their
 * session.
 */
export function resolveImpersonationFromToken(
  tokenResult: ImpersonationTokenResult
): ImpersonationInfo | null {
  // A member's own sign-in (password, Google, etc.) must never be treated as
  // impersonation, even if stale impersonation claims remain on the account.
  if (tokenResult.signInProvider !== "custom") {
    return null;
  }

  const claims = tokenResult.claims;
  const impersonatedBy = claims.impersonatedBy;
  if (typeof impersonatedBy !== "string" || impersonatedBy.length === 0) {
    return null;
  }

  const impersonatedBusiness = claims.impersonatedBusiness;
  const impersonatedByEmail = claims.impersonatedByEmail;
  return {
    impersonatedBy,
    impersonatedByEmail:
      typeof impersonatedByEmail === "string" ? impersonatedByEmail : null,
    impersonatedBusiness:
      typeof impersonatedBusiness === "string" ? impersonatedBusiness : null,
  };
}
